import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setPrescription,
  clearPrescription,
  extractPrescriptionFromImage,
  clearExtractError,
} from '../store/prescriptionSlice';
import { updateUserInfo } from '../store/userSlice';
import axiosInstance from '../api/axiosInstance';
import PowerStepper from './PowerStepper';
import { toast } from 'react-toastify';
import { FiUpload, FiEdit3, FiX, FiCheck, FiAlertCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';

// ── Prescription math ─────────────────────────────────────────────────────────

/**
 * Reduce absolute power for high-power CL prescriptions:
 * - |power| ≥ 6 and ≤ 10 → subtract 0.5
 * - |power| > 10          → subtract 1
 */
function applyHighPowerReduction(power) {
  if (power === null) return null;
  const abs = Math.abs(power);
  const sign = power < 0 ? -1 : 1;
  if (abs > 10) return r25(sign * (abs - 1));
  if (abs >= 6)  return r25(sign * (abs - 0.5));
  return power;
}

/**
 * Calculate contact lens power from glasses prescription:
 * - No CYL: apply high-power reduction to SPH as-is
 * - With CYL: Spherical Equivalent = round25(SPH + CYL/2), then apply high-power reduction
 */
function calculateCLPower(sph, cyl) {
  if (sph === null) return null;
  const c = cyl ?? 0;
  const effective = c === 0 ? sph : r25(sph + c / 2);
  return applyHighPowerReduction(effective);
}

/**
 * Returns true when Toric lenses are preferred:
 * cylinder magnitude exceeds sphere magnitude.
 */
function isToricRecommended(sph, cyl) {
  if (sph === null || !cyl || cyl === 0) return false;
  return Math.abs(cyl) > Math.abs(sph);
}

// ── Toric lens snapping ───────────────────────────────────────────────────────
const TORIC_CYL_STEPS = [0.75, 1.25, 1.75, 2.25, 2.75];

/** Snap CYL to nearest valid Toric step (round absolute value DOWN). Max 2.75. */
function snapToricCyl(cyl) {
  if (!cyl || cyl === 0) return null;
  const abs = Math.abs(cyl);
  const sign = cyl < 0 ? -1 : 1;
  let snapped = TORIC_CYL_STEPS[0];
  for (const step of TORIC_CYL_STEPS) {
    if (step <= abs) snapped = step;
    else break;
  }
  return sign * snapped;
}

/** Round axis to nearest 10. */
function snapToricAxis(axis) {
  if (axis === null || axis === undefined) return null;
  return Math.round(axis / 10) * 10;
}

// Round to nearest 0.25
const r25 = (n) => (n === null ? null : Math.round(n * 4) / 4);

// Format SPH for display
const fmtSph = (n) => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return 'Plano';
  const abs = Math.abs(n).toFixed(2);
  return n > 0 ? `+${abs}` : `-${abs}`;
};

const initialEye = { sph: null, cyl: null, axis: null };

// ── Famous conditions that require manual entry (not derivable from Rx alone) ──
// Anisometropia and monovision are excluded: they're already captured by entering
// different SPH values per eye — no extra selection needed.
const FAMOUS_CONDITIONS = [
  { id: 'none',       label: 'None / standard prescription', labelAr: 'لا يوجد / وصفة عادية' },
  { id: 'presbyopia', label: 'Presbyopia — طول النظر الشيخوخي', labelAr: 'صعوبة الرؤية عن قرب مع التقدم في السن', hasAdd: true },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function EyePowerFinder({ onClose, onApplied }) {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const saved = useSelector((s) => s.prescription);
  const { extractLoading, extractError } = saved;

  // Pre-populate from saved Redux state if a prescription was previously entered
  const stripEffective = (eye) => {
    const { effectiveSph, ...rest } = eye || {};
    return { ...initialEye, ...rest };
  };

  const [tab, setTab]           = useState('manual');
  const [sameEyes, setSameEyes] = useState(false);

  const [rightEye, setRightEye] = useState(() => saved.isSet ? stripEffective(saved.rightEye) : { ...initialEye });
  const [leftEye,  setLeftEye]  = useState(() => saved.isSet ? stripEffective(saved.leftEye)  : { ...initialEye });

  // Condition / presbyopia
  const [condition, setCondition]     = useState(() => saved.isSet ? (saved.condition || 'none') : 'none');
  const [addPower, setAddPower]       = useState(() => saved.isSet ? (saved.addPower || null) : null);
  const [showConditions, setShowConditions] = useState(false);

  // Upload state
  const [uploadDragging, setUploadDragging] = useState(false);
  const [uploadFileName, setUploadFileName] = useState(null);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [extractedPreview, setExtractedPreview] = useState(null);
  const fileInputRef = useRef(null);

  // If the saved prescription hydrates AFTER this mounts (e.g. account data
  // arrives from getMe on a direct page load), populate the form — but only if
  // the user hasn't already typed anything.
  const hydratedRef = useRef(saved.isSet);
  useEffect(() => {
    if (saved.isSet && !hydratedRef.current) {
      setRightEye((cur) => (cur.sph == null ? stripEffective(saved.rightEye) : cur));
      setLeftEye((cur) => (cur.sph == null ? stripEffective(saved.leftEye) : cur));
      setCondition((c) => (c === 'none' ? (saved.condition || 'none') : c));
      setAddPower((a) => (a == null ? (saved.addPower || null) : a));
      hydratedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved.isSet]);

  // ── Calculate effective CL powers (automatic — no toggles) ────────────────
  const effectiveRight = useMemo(() => calculateCLPower(rightEye.sph, rightEye.cyl), [rightEye]);
  const effectiveLeft  = useMemo(() => calculateCLPower(leftEye.sph,  leftEye.cyl),  [leftEye]);

  const toricRight = useMemo(() => isToricRecommended(rightEye.sph, rightEye.cyl), [rightEye]);
  const toricLeft  = useMemo(() => isToricRecommended(leftEye.sph,  leftEye.cyl),  [leftEye]);

  const toricOrderRight = useMemo(() => toricRight ? {
    sph: rightEye.sph, cyl: snapToricCyl(rightEye.cyl), axis: snapToricAxis(rightEye.axis),
  } : null, [rightEye, toricRight]);
  const toricOrderLeft = useMemo(() => toricLeft ? {
    sph: leftEye.sph, cyl: snapToricCyl(leftEye.cyl), axis: snapToricAxis(leftEye.axis),
  } : null, [leftEye, toricLeft]);

  // ── Stepper handlers ───────────────────────────────────────────────────────
  const updateRight = (field, value) => {
    const updated = { ...rightEye, [field]: value };
    // Auto-clear axis when CYL is removed or zeroed — axis has no meaning without cylinder
    if (field === 'cyl' && (value === null || value === 0)) updated.axis = null;
    setRightEye(updated);
    if (sameEyes) setLeftEye({ ...updated });
  };

  const updateLeft = (field, value) => {
    setLeftEye((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'cyl' && (value === null || value === 0)) updated.axis = null;
      return updated;
    });
  };

  const handleSameEyes = (checked) => {
    setSameEyes(checked);
    if (checked) setLeftEye({ ...rightEye });
  };

  // ── Upload handlers ────────────────────────────────────────────────────────
  const processFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10 MB.');
      return;
    }
    setUploadFileName(file.name);
    setUploadProcessing(true);
    setExtractedPreview(null);
    dispatch(clearExtractError());
    try {
      const result = await dispatch(extractPrescriptionFromImage(file)).unwrap();
      setExtractedPreview(result);
      if (result.rightEye) setRightEye(result.rightEye);
      if (result.leftEye)  setLeftEye(result.leftEye);
      setTab('manual');
      toast.success('Prescription extracted! Please review and confirm below.');
    } catch (err) {
      toast.error(err || 'Could not extract prescription. Please try entering it manually.');
    } finally {
      setUploadProcessing(false);
    }
  }, [dispatch]);

  const handleFileChange = (e) => { if (e.target.files[0]) processFile(e.target.files[0]); };
  const handleDrop = (e) => {
    e.preventDefault();
    setUploadDragging(false);
    if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
  };

  // ── Apply prescription ─────────────────────────────────────────────────────
  const handleApply = () => {
    const hasRight = rightEye.sph !== null;
    const hasLeft  = leftEye.sph  !== null;
    if (!hasRight && !hasLeft) {
      toast.error("Please enter at least one eye's sphere (SPH) value.");
      return;
    }

    // Save raw prescription AND calculated effectiveSph (SE / vertex) as a guide only.
    // effectiveSph is shown as a suggestion on product pages — never used for filtering.
    const toSave = {
      rightEye: { ...rightEye, effectiveSph: effectiveRight },
      leftEye:  { ...leftEye,  effectiveSph: effectiveLeft  },
      condition,
      addPower: condition === 'presbyopia' ? addPower : null,
      method: tab === 'upload' || extractedPreview ? 'upload' : 'manual',
    };

    dispatch(setPrescription(toSave));

    // Persist to the user's account when logged in, so it auto-applies next time.
    if (localStorage.getItem('token')) {
      axiosInstance.put('/users/updateMe', {
        prescription: {
          rightEye: { sph: rightEye.sph, cyl: rightEye.cyl, axis: rightEye.axis },
          leftEye:  { sph: leftEye.sph,  cyl: leftEye.cyl,  axis: leftEye.axis },
          method: toSave.method,
        },
      })
        .then(({ data }) => dispatch(updateUserInfo(data.data)))
        .catch(() => { /* non-blocking: local Redux already updated */ });
    }

    toast.success('Prescription saved! Filtering compatible lenses...');
    onApplied?.();
    navigate('/products/colored-lenses');
  };

  const handleClear = () => {
    setRightEye({ ...initialEye });
    setLeftEye({ ...initialEye });
    setExtractedPreview(null);
    setUploadFileName(null);
    setCondition('none');
    setAddPower(null);
    dispatch(clearPrescription());
  };

  // ── Render ────────────────────────────────────────────────────���────────────
  return (
    <div className="bg-white rounded-2xl shadow-card w-full max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-sand">
        <div>
          <h2 className="text-xl font-display font-normal tracking-tight text-ink">Eye Power Finder</h2>
          <p className="text-sm text-clay mt-0.5">Enter your prescription to find compatible lenses</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-full hover:bg-cream text-clay hover:text-ink transition-colors" aria-label="Close">
            <FiX size={20} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex px-6 pt-4 gap-2">
        <TabButton active={tab === 'manual'} onClick={() => setTab('manual')} icon={<FiEdit3 size={15} />}>
          Enter Manually
        </TabButton>
        <TabButton active={tab === 'upload'} onClick={() => setTab('upload')} icon={<FiUpload size={15} />}>
          Upload Prescription
        </TabButton>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-5 space-y-5">
        {tab === 'manual' && (
          <ManualTab
            rightEye={rightEye}
            leftEye={leftEye}
            sameEyes={sameEyes}
            onUpdateRight={updateRight}
            onUpdateLeft={updateLeft}
            onSameEyes={handleSameEyes}
            extractedPreview={extractedPreview}
          />
        )}
        {tab === 'upload' && (
          <UploadTab
            dragging={uploadDragging}
            setDragging={setUploadDragging}
            fileName={uploadFileName}
            processing={uploadProcessing}
            error={extractError}
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            onDrop={handleDrop}
          />
        )}

        {/* ── Calculation Adjustments ── */}
        {tab === 'manual' && (
          <CalculationAdjustments
            condition={condition}
            onConditionChange={setCondition}
            addPower={addPower}
            onAddPowerChange={setAddPower}
            showConditions={showConditions}
            onToggleConditions={() => setShowConditions((v) => !v)}
          />
        )}

        {/* ── Effective Search Power summary ── */}
        {tab === 'manual' && (rightEye.sph !== null || leftEye.sph !== null) && (
          <EffectivePowerSummary
            rightEye={rightEye}
            leftEye={leftEye}
            effectiveRight={effectiveRight}
            effectiveLeft={effectiveLeft}
            toricRight={toricRight}
            toricLeft={toricLeft}
            toricOrderRight={toricOrderRight}
            toricOrderLeft={toricOrderLeft}
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleApply}
          disabled={uploadProcessing}
          className="flex-1 bg-ink text-cream font-semibold py-3 px-6 rounded-xl
                     hover:bg-brand-500 hover:text-ink active:scale-95 transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Find My Lenses
        </button>
        <button
          onClick={handleClear}
          className="sm:w-auto px-4 py-3 rounded-xl border border-sand text-clay font-medium
                     hover:bg-cream transition-colors text-sm"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

// ── Calculation Adjustments Panel ─────────────────────────────────────────────
function CalculationAdjustments({
  condition, onConditionChange,
  addPower, onAddPowerChange,
  showConditions, onToggleConditions,
}) {
  return (
    <div className="space-y-3">
      {/* Eye condition accordion */}
      <div className="rounded-xl border border-sand overflow-hidden">
        <button
          type="button"
          onClick={onToggleConditions}
          className="w-full flex items-center justify-between px-4 py-3 text-sm text-clay hover:bg-cream transition-colors"
        >
          <span className="font-medium flex items-center gap-2">
            <span>Do you have a special eye condition?</span>
            {condition !== 'none' && (
              <span className="text-xs bg-brand-500 text-ink px-2 py-0.5 rounded-full">
                {FAMOUS_CONDITIONS.find(c => c.id === condition)?.label.split('—')[0].trim()}
              </span>
            )}
          </span>
          {showConditions ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
        </button>

        {showConditions && (
          <div className="border-t border-sand px-4 py-3 space-y-2">
            <p className="text-xs text-clay">
              Only select if your doctor told you about a specific condition. Most people can skip this.
            </p>
            <div className="space-y-2">
              {FAMOUS_CONDITIONS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onConditionChange(c.id)}
                  className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all ${
                    condition === c.id
                      ? 'border-brand-500 bg-brand-50'
                      : 'border-sand hover:border-brand-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${condition === c.id ? 'text-brand-700' : 'text-clay'}`}>
                      {c.label}
                    </span>
                    {condition === c.id && (
                      <FiCheck size={15} className="text-brand-600 shrink-0" />
                    )}
                  </div>
                  {c.labelAr && (
                    <p className="text-xs text-clay mt-0.5" dir="rtl">{c.labelAr}</p>
                  )}
                  {CONDITION_DESCRIPTIONS[c.id] && condition === c.id && (
                    <p className="text-xs text-clay mt-1">{CONDITION_DESCRIPTIONS[c.id]}</p>
                  )}
                </button>
              ))}
            </div>

            {/* ADD power input for presbyopia */}
            {condition === 'presbyopia' && (
              <div className="mt-2 pt-3 border-t border-sand">
                <p className="text-sm font-medium text-clay mb-1">Reading Addition (ADD)</p>
                <p className="text-xs text-clay mb-2">
                  This is the extra power written on your prescription for reading. Usually between +0.75 and +3.50.
                </p>
                <PowerStepper
                  label="ADD Power"
                  value={addPower}
                  onChange={onAddPowerChange}
                  min={0.75}
                  max={3.5}
                  step={0.25}
                  formatter={(n) => `+${n.toFixed(2)}`}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Effective Search Power Summary ────────────────────────────────────────────
function EffectivePowerSummary({
  rightEye, leftEye, effectiveRight, effectiveLeft,
  toricRight, toricLeft, toricOrderRight, toricOrderLeft,
}) {
  const changed  = (raw, eff) => raw !== null && eff !== null && raw !== eff;
  const anyToric = toricRight || toricLeft;

  return (
    <div className="rounded-xl bg-ink text-cream px-4 py-3 space-y-3">
      {/* Toric recommendation banner */}
      {anyToric && (
        <div className="rounded-lg bg-amber-500/20 border border-amber-400/40 px-3 py-2.5">
          <p className="text-xs font-semibold text-amber-300 mb-0.5">Toric Lenses Recommended (Astigmatism)</p>
          <p className="text-xs text-amber-200/80">
            Your cylinder is higher than your sphere. Toric lenses are preferred — order with the values below.
          </p>
        </div>
      )}

      {/* ── Toric order details ── */}
      {anyToric && (
        <div>
          <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-2">Toric Lens Order</p>
          <div className="grid grid-cols-2 gap-3">
            {toricOrderRight ? (
              <ToricEyeCell label="Right Eye (OD)" order={toricOrderRight} />
            ) : (
              <EyePowerCell
                label="Right Eye (OD)" rawSph={rightEye.sph}
                effectiveSph={effectiveRight} changed={changed(rightEye.sph, effectiveRight)}
              />
            )}
            {toricOrderLeft ? (
              <ToricEyeCell label="Left Eye (OS)" order={toricOrderLeft} />
            ) : (
              <EyePowerCell
                label="Left Eye (OS)" rawSph={leftEye.sph}
                effectiveSph={effectiveLeft} changed={changed(leftEye.sph, effectiveLeft)}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Non-Toric / SE power ── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {anyToric ? 'Non-Toric Alternative' : 'Contact Lens Power'}
          </p>
          <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">Guide only</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EyePowerCell
            label="Right Eye (OD)" rawSph={rightEye.sph}
            effectiveSph={effectiveRight} changed={changed(rightEye.sph, effectiveRight)}
          />
          <EyePowerCell
            label="Left Eye (OS)" rawSph={leftEye.sph}
            effectiveSph={effectiveLeft} changed={changed(leftEye.sph, effectiveLeft)}
          />
        </div>
      </div>

      {anyToric && (
        <p className="text-xs text-amber-300/70 pt-1 border-t border-gray-700">
          Without Toric lenses, estimated vision quality: ~60–70%
        </p>
      )}
      {!anyToric && (
        <p className="text-xs text-gray-500 pt-1 border-t border-gray-700">
          These values are calculated from your glasses prescription. Use them as a guide when selecting lenses.
        </p>
      )}
    </div>
  );
}

function ToricEyeCell({ label, order }) {
  return (
    <div>
      <p className="text-xs text-amber-300/70 mb-1">{label}</p>
      <div className="space-y-0.5 text-sm font-mono">
        <p>SPH: <span className="font-bold">{fmtSph(order.sph)}</span></p>
        <p>CYL: <span className="font-bold">{fmtSph(order.cyl)}</span></p>
        <p>Axis: <span className="font-bold">{order.axis != null ? `${order.axis}°` : '—'}</span></p>
      </div>
    </div>
  );
}

function EyePowerCell({ label, rawSph, effectiveSph, changed }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-lg font-bold font-mono">{fmtSph(effectiveSph)}</p>
      {changed && (
        <p className="text-xs text-gray-500 mt-0.5">glasses: {fmtSph(rawSph)}</p>
      )}
    </div>
  );
}

// ── Condition descriptions ────────────────────────────────────────────────────
const CONDITION_DESCRIPTIONS = {
  none:       'Standard single-vision prescription — no special adjustments needed.',
  presbyopia: 'Age-related difficulty focusing up close. You may have an ADD power on your prescription. · قد يكون لديك قيمة ADD في وصفتك الطبية.',
};

// ── Sub-components ────────────────────────��──────────────────────────────────��
function TabButton({ active, onClick, icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors
        ${active ? 'bg-ink text-cream' : 'bg-cream text-clay hover:bg-sand'}`}
    >
      {icon}{children}
    </button>
  );
}

function ManualTab({ rightEye, leftEye, sameEyes, onUpdateRight, onUpdateLeft, onSameEyes, extractedPreview }) {
  return (
    <div className="space-y-5">
      {extractedPreview && (
        <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800">
          <FiCheck className="shrink-0 mt-0.5" size={16} />
          <span>Prescription extracted from your upload. Review and adjust values below if needed.</span>
        </div>
      )}

      <p className="text-xs text-clay">
        Enter your prescription values. <strong>SPH</strong> is required.{' '}
        <strong>CYL</strong> and <strong>Axis</strong> are optional — most colored lenses are spherical only.
        If your CYL is entered, the Spherical Equivalent will be calculated automatically below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <EyeColumn
          label="Right Eye (OD)"
          eye={rightEye}
          onUpdate={onUpdateRight}
          accentClass="border-brand-200 bg-brand-50"
          labelClass="text-brand-700"
        />
        <EyeColumn
          label="Left Eye (OS)"
          eye={leftEye}
          onUpdate={sameEyes ? null : onUpdateLeft}
          disabled={sameEyes}
          accentClass="border-sand bg-cream"
          labelClass="text-clay"
        />
      </div>

      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={sameEyes}
          onChange={(e) => onSameEyes(e.target.checked)}
          className="w-4 h-4 rounded accent-brand-500"
        />
        <span className="text-sm text-clay">Same prescription for both eyes</span>
      </label>
    </div>
  );
}

function EyeColumn({ label, eye, onUpdate, disabled = false, accentClass, labelClass }) {
  return (
    <div className={`rounded-xl border-2 p-4 ${accentClass}`}>
      <h3 className={`text-sm font-bold mb-4 ${labelClass}`}>{label}</h3>
      <div className="space-y-4">
        <PowerStepper
          label="SPH (Sphere)"
          value={eye.sph}
          onChange={(v) => onUpdate?.('sph', v)}
          min={-20} max={20} step={0.25}
          disabled={disabled}
        />
        <PowerStepper
          label="CYL (Cylinder)"
          value={eye.cyl}
          onChange={(v) => onUpdate?.('cyl', v)}
          min={-6} max={6} step={0.25}
          disabled={disabled}
        />
        <PowerStepper
          label="Axis"
          value={eye.axis}
          onChange={(v) => onUpdate?.('axis', v)}
          min={0} max={180} step={1}
          disabled={disabled || !eye.cyl}
          formatter={(n) => `${n}°`}
        />
      </div>
      {/* Summary */}
      <div className="mt-4 pt-3 border-t border-current border-opacity-20 text-xs text-clay space-y-0.5">
        <div>SPH: <strong>{fmtSph(eye.sph)}</strong></div>
        <div>CYL: <strong>{eye.cyl !== null ? fmtSph(eye.cyl) : '—'}</strong></div>
        <div>Axis: <strong>{eye.axis !== null ? `${eye.axis}°` : '—'}</strong></div>
      </div>
    </div>
  );
}

function UploadTab({ dragging, setDragging, fileName, processing, error, fileInputRef, onFileChange, onDrop }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-clay">
        Upload a photo or scan of your prescription. We'll extract the values automatically using AI.
        Supports <strong>any image format or PDF</strong> (max 10 MB).
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors
          flex flex-col items-center justify-center py-12 px-6 text-center
          ${dragging ? 'border-brand-500 bg-brand-50' : 'border-sand hover:border-brand-300 bg-white'}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={onFileChange}
          className="hidden"
        />
        {processing ? (
          <div className="flex flex-col items-center gap-3 text-clay">
            <div className="animate-spin w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full" />
            <p className="text-sm font-medium">Reading your prescription...</p>
            <p className="text-xs text-clay">This usually takes a few seconds</p>
          </div>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center mb-3">
              <FiUpload size={24} className="text-clay" />
            </div>
            {fileName ? (
              <p className="text-sm font-medium text-ink">{fileName}</p>
            ) : (
              <>
                <p className="text-sm font-semibold text-ink">
                  Drag & drop or <span className="underline">tap to upload</span>
                </p>
                <p className="text-xs text-clay mt-1">Any image · PDF — max 10 MB</p>
              </>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <FiAlertCircle className="shrink-0 mt-0.5" size={16} />
          <div>
            <p className="font-medium">{error}</p>
            <p className="text-xs mt-1 text-red-500">Please try again or enter your prescription manually.</p>
          </div>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800">
        <strong>Tip:</strong> Make sure your prescription document is clearly visible and not blurry.
        A well-lit photo of your prescription paper works great.
      </div>
    </div>
  );
}
